import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Button,
  Text,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import {
  FaAt,
  FaScroll,
  FaHashtag,
  FaSmile,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { trackBasicEvent } from "../../../_utils/eventsTracker";
import { isFeatureAvailable } from "../../../_utils/common";
import { FeatureCodes } from "../../details/constants";
import AdvancedMessageField from "../AdvancedMessageField/AdvancedMessageField";
import AdvancedMessageToolbarButton from "../AdvancedMessageField/AdvancedMessageToolbarButton";
import {
  AdvancedMessageAtMentionsCounter,
  AdvancedMessageHashtagsCounter,
} from "../AdvancedMessageField/AdvancedMessageCounter";
import EmojiPicker from "../EmojiPicker/EmojiPicker";
import SnippetsPicker from "../SnippetsPicker/SnippetsPicker";
import useAppFeatures from "../../../_hooks/useAppFeatures";

const messageWrapperStyles = {
  position: "relative",
  borderRadius: "lg",
  background: "blackAlpha.300",
};

const toolbarStyles = {
  display: "flex",
  flexDirection: "row-reverse",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "1",
  marginBottom: "2",
};

const customFieldsStyles = {
  paddingX: "2",
  paddingY: "1",
};

const customFieldRowStyles = {
  display: "flex",
  alignItems: "center",
  marginY: "1",
};

const customFieldLabelStyles = {
  paddingRight: "2",
  fontSize: "sm",
  lineHeight: "1.1",
};

const customFieldContentStyles = {
  display: "flex",
  alignItems: "center",
};

const getCorrectDelay = (value, min, max) => {
  const intDelay = parseInt(value || 0, 10);
  const fixedMax = max && intDelay > max ? max : intDelay;
  return min && fixedMax < min ? min : fixedMax;
};

const getInitialState = (data, minDelay, maxDelay) => ({
  ...data,
  delay: getCorrectDelay(data.delay, minDelay, maxDelay),
});

const PostEditComment = ({
  data,
  minDelay,
  maxDelay,
  onSave,
  onCancel,
  messageCounter,
  highlightLinks,
  highlightHashtags,
  highlightAtMentions,
  linkMenuEnabled,
  onToggleLinkDisabling,
  getLinkData,
}) => {
  const { t } = useTranslation(["planner", "common"]);
  const messageRef = useRef(null);
  const [appFeatures] = useAppFeatures();
  const [workspaceFeatures, setWorkspaceFeatures] = useState({
    snippets: false,
  });
  const [commentData, setCommentData] = useState(getInitialState(data, minDelay, maxDelay));
  const subscriptionData = useSelector((state) => state.subscription);

  const insertTextToMessage = useCallback(
    (text) => {
      const view = messageRef.current?.view;

      if (!view) return;

      view.dispatch(view.state.tr.insertText(text));
    },
    [messageRef]
  );

  const handleMessageChange = useCallback(
    ({ message }) => {
      setCommentData({
        ...commentData,
        message,
      });
    },
    [commentData]
  );

  const handleDelayChange = useCallback(
    (value) => {
      setCommentData({
        ...commentData,
        delay: parseInt(value || 0, 10),
      });
    },
    [commentData]
  );

  const handleSave = useCallback(() => {
    onSave?.(commentData);
  }, [commentData, onSave]);

  const handleEmojiSelect = useCallback(
    (emoji) => {
      insertTextToMessage(emoji.native);

      trackBasicEvent("post-edit-comment", "click", "emoji - use");
    },
    [insertTextToMessage]
  );

  const handleSnippetSelect = useCallback(
    (snippet) => {
      insertTextToMessage(snippet.text);

      trackBasicEvent("post-edit-comment", "click", "snippet - use");
    },
    [insertTextToMessage]
  );

  useEffect(() => {
    setWorkspaceFeatures({
      snippets: isFeatureAvailable(
        subscriptionData,
        FeatureCodes.PLANNER_POST_SNIPPETS
      ),
    });
  }, [subscriptionData]);

  return (
    <Box>
      <Box>
        <Box sx={customFieldsStyles}>
          <Box sx={customFieldRowStyles}>
            <Box sx={customFieldLabelStyles}>
              {`${t("post_edit_comments.delay.title")}:`}
            </Box>
            <Box sx={customFieldContentStyles}>
              <NumberInput
                size="sm"
                width="5rem"
                value={commentData?.delay || 0}
                min={minDelay || 0}
                max={maxDelay && maxDelay <= minDelay ? minDelay : maxDelay}
                precision={0}
                step={1}
                keepWithinRange
                onChange={handleDelayChange}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text
                as="span"
                marginLeft="2"
                verticalAlign="middle"
                fontSize="sm"
              >
                {t("post_edit_comments.delay.suffix")}
              </Text>
            </Box>
          </Box>
        </Box>
        <Box sx={messageWrapperStyles}>
          <AdvancedMessageField
            ref={messageRef}
            state={commentData}
            placeholder={t("post_edit_comments.message.placeholder")}
            onChange={handleMessageChange}
            highlightLinks={highlightLinks}
            highlightHashtags={highlightHashtags}
            highlightAtMentions={highlightAtMentions}
            linkMenuEnabled={linkMenuEnabled}
            onToggleLinkDisabling={onToggleLinkDisabling}
            getLinkData={getLinkData}
          />
          <Box sx={toolbarStyles}>
            <Stack isInline align="center" spacing="1" marginLeft="auto">
              {messageCounter ? messageCounter({ state: commentData }) : null}
              {highlightHashtags ? (
                <AdvancedMessageHashtagsCounter
                  state={commentData}
                  leftIcon={FaHashtag}
                />
              ) : null}
              {highlightAtMentions ? (
                <AdvancedMessageAtMentionsCounter
                  state={commentData}
                  leftIcon={FaAt}
                />
              ) : null}
            </Stack>
            <Stack isInline align="center" spacing="1">
              {appFeatures.snippets ? (
                <Popover isLazy placement="bottom-start">
                  <PopoverTrigger>
                    <AdvancedMessageToolbarButton
                      tooltipLabel={t("post_editor.toolbar.snippets")}
                      icon={FaScroll}
                      upgradeRequired={!workspaceFeatures.snippets}
                    />
                  </PopoverTrigger>
                  <PopoverContent sx={{ background: "gray.600" }}>
                    <SnippetsPicker onSelect={handleSnippetSelect} />
                  </PopoverContent>
                </Popover>
              ) : null}
              <Popover isLazy placement="bottom-start">
                <PopoverTrigger>
                  <AdvancedMessageToolbarButton
                    tooltipLabel={t("post_editor.toolbar.emoji")}
                    icon={FaSmile}
                  />
                </PopoverTrigger>
                <PopoverContent
                  width="auto"
                  lineHeight="0"
                  sx={{ background: "gray.600" }}
                >
                  <EmojiPicker onSelect={handleEmojiSelect} />
                </PopoverContent>
              </Popover>
            </Stack>
          </Box>
        </Box>
      </Box>
      <Box display="flex" marginTop="3">
        <Button
          size="sm"
          variant="secondary"
          onClick={onCancel}
        >
          {t("cancel", { ns: "common" })}
        </Button>
        <Button
          size="sm"
          variant="solid"
          isDisabled={!commentData?.message}
          onClick={handleSave}
          marginLeft="auto"
        >
          {t("save", { ns: "common" })}
        </Button>
      </Box>
    </Box>
  );
};

PostEditComment.propTypes = {
  data: PropTypes.shape({
    message: PropTypes.string.isRequired,
    delay: PropTypes.number,
  }),
  minDelay: PropTypes.number,
  maxDelay: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  messageCounter: PropTypes.func,
  highlightLinks: PropTypes.bool,
  highlightHashtags: PropTypes.bool,
  highlightAtMentions: PropTypes.bool,
  linkMenuEnabled: PropTypes.bool,
  onToggleLinkDisabling: PropTypes.func,
  getLinkData: PropTypes.func,
};

PostEditComment.defaultProps = {
  data: undefined,
  minDelay: undefined,
  maxDelay: undefined,
  messageCounter: undefined,
  highlightLinks: undefined,
  highlightHashtags: undefined,
  highlightAtMentions: undefined,
  linkMenuEnabled: undefined,
  onToggleLinkDisabling: undefined,
  getLinkData: undefined,
};

export default memo(PostEditComment);
